import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkSessionStatus, doLogout } from '../redux/AuthActions';
import { callStatus } from '../redux/constants';
import { doSetUsage, sessionExpired } from '../redux/SessionActions';
import store from '../redux/store';
import useMessageCommunication from './useMessageCommunication';

const useForceLogout = () => {
  const dispatch = useDispatch();
  const { postMessage } = useMessageCommunication();

  const auth = useSelector(({ auth }) => auth);
  const session = useSelector(({ session }) => session);
  const translations = useSelector(({ translations }) => translations);
  const context = useSelector(({ context }) => context);
  const slMainForceLogoutTimeoutMins = useSelector(
    ({
      main: {
        data: { logout_timeout_mins },
      },
    }) => logout_timeout_mins
  );

  let intervalCheck = useRef();
  let intervalUsage = useRef();
  let modalOpen = useRef(false);
  let forceLogout = useRef(false);
  let hasEventListener = useRef(false);

  const [slMainForceLogout, setSlMainForceLogout] = useState(null);

  useEffect(() => {
    let forceLogout = null;
    if (
      context?.configVars?.SL_MAIN_FORCE_LOGOUT != null &&
      slMainForceLogoutTimeoutMins != null
    ) {
      forceLogout =
        context?.configVars?.SL_MAIN_FORCE_LOGOUT > slMainForceLogoutTimeoutMins
          ? context?.configVars?.SL_MAIN_FORCE_LOGOUT
          : slMainForceLogoutTimeoutMins;
    } else if (context?.configVars?.SL_MAIN_FORCE_LOGOUT != null) {
      forceLogout = context?.configVars?.SL_MAIN_FORCE_LOGOUT;
    } else if (slMainForceLogoutTimeoutMins != null) {
      forceLogout = slMainForceLogoutTimeoutMins;
    }

    if (forceLogout) {
      setSlMainForceLogout(forceLogout);
      console.log(
        '[Config] Logout timeout:',
        forceLogout,
        'min',
        '| SL_MAIN_FORCE_LOGOUT:',
        context?.configVars?.SL_MAIN_FORCE_LOGOUT ?? 'not set',
        '| logout_timeout_mins (API):',
        slMainForceLogoutTimeoutMins ?? 'not set',
        '| SL_MAIN_CHECK_SESSION_STATUS_TIME:',
        context?.configVars?.SL_MAIN_CHECK_SESSION_STATUS_TIME ??
          '300000 (default)',
        '| SL_MAIN_UPDATE_USAGE_TIME:',
        context?.configVars?.SL_MAIN_UPDATE_USAGE_TIME ?? '150000 (default)'
      );
    }
  }, [context, slMainForceLogoutTimeoutMins]);

  // Validate if another user logged in or if logout
  useEffect(() => {
    if (session.status === callStatus.SUCCESS) {
      const startIntervalCheck = () => {
        intervalCheck.current = setInterval(async () => {
          const res = await checkSessionStatus();
          const dataAuth = store.getState().auth;
          const dataSession = store.getState().auth;
          if (
            dataAuth.status === callStatus.SUCCESS &&
            dataSession.status === callStatus.SUCCESS &&
            res?.data?.user &&
            !isEqual(res.data.user, dataAuth.data['new_user']) &&
            !isEqual(res.data.user, dataAuth.data['user'])
          ) {
            modalOpen.current = true;
            const messageContent = res.data.is_authenticated
              ? translations.data.login.another_user_logged_in
              : translations.data.login.user_logged_out;

            console.log(
              '[Logout] Session change detected (polling):',
              res.data.is_authenticated
                ? 'another user logged in'
                : 'user logged out'
            );
            dispatch(sessionExpired(messageContent));
          }
        }, context?.configVars?.SL_MAIN_CHECK_SESSION_STATUS_TIME || 300000);
      };
      !intervalCheck.current && startIntervalCheck();

      const startIntervalUsage = () => {
        intervalUsage.current = setInterval(async () => {
          dispatch(doSetUsage());
        }, context?.configVars?.SL_MAIN_UPDATE_USAGE_TIME || 150000);
      };
      !intervalUsage.current && startIntervalUsage();
    }

    if (
      session.status === callStatus.PENDING ||
      session.status === callStatus.EXPIRED ||
      session.status === callStatus.FAILED_GET
    ) {
      intervalCheck.current && clearInterval(intervalCheck.current);
      intervalCheck.current = null;
      intervalUsage.current && clearInterval(intervalUsage.current);
      intervalUsage.current = null;
    }

    // eslint-disable-next-line
  }, [session]);

  // Force logout by timestamp
  useEffect(() => {
    if (
      auth.status === callStatus.SUCCESS &&
      session.status === callStatus.SUCCESS
    ) {
      const forceLogoutTimestamp = localStorage.getItem('lastActivity');

      if (slMainForceLogout && forceLogoutTimestamp) {
        const timeDiff = forceLogoutTimestamp
          ? Math.abs(Date.now() - parseInt(forceLogoutTimestamp)) / 1000
          : undefined;

        if (timeDiff && timeDiff > slMainForceLogout * 60) {
          console.log(
            '[Logout] Inactivity timeout: no activity for',
            Math.round(timeDiff / 60),
            `min (timeout: ${slMainForceLogout} min)`
          );
          dispatch(doLogout());
          forceLogout.current = false;
          postMessage({ cmd: 'on_logout', params: {} });
        }
      }
    }
  }, [dispatch, auth.status, session.status, slMainForceLogout, postMessage]);

  useEffect(() => {
    const handleTabClose = (event) => {
      if (forceLogout.current) {
        localStorage.setItem('lastActivity', Date.now());
      }
    };

    window.addEventListener('beforeunload', handleTabClose);

    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!hasEventListener.current) {
      document.addEventListener('visibilitychange', async () => {
        const dataAuth = store.getState().auth;

        if (!document.hidden && dataAuth.status === callStatus.SUCCESS) {
          hasEventListener.current = true;

          if (!modalOpen.current) {
            const res = await checkSessionStatus();
            if (
              res?.data?.user &&
              !isEqual(res.data.user, dataAuth.data['new_user']) &&
              !isEqual(res.data.user, dataAuth.data['user'])
            ) {
              modalOpen.current = true;
              const messageContent = res.data.is_authenticated
                ? translations.data.login.another_user_logged_in
                : translations.data.login.user_logged_out;

              console.log(
                '[Logout] Session change detected (visibilitychange):',
                res.data.is_authenticated
                  ? 'another user logged in'
                  : 'user logged out'
              );
              dispatch(sessionExpired(messageContent));
            }
          }
        }
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (auth.status === callStatus.SUCCESS) {
      if (!forceLogout.current && slMainForceLogout && slMainForceLogout > 0) {
        forceLogout.current = true;
      }
    }
    // eslint-disable-next-line
  }, [auth.status, slMainForceLogout, forceLogout]);

  return { modalOpen, forceLogout };
};

export default useForceLogout;
